export const EVENT_END_BEFORE_START_ERROR =
  "End date cannot be prior to the start date.";

export const EVENT_END_TIME_BEFORE_START_ERROR = "End time is before the start time.";

export const EVENT_START_BEFORE_TODAY_ERROR =
  "Start date cannot be prior to today's date.";

export const PUBLISH_AFTER_EVENT_START_ERROR =
  "Scheduled publish time cannot be after the event start";

/** A time of day as the wizard keeps it: "9:00" or "18:30". */
export const TIME_OF_DAY = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;

const timeToMinutes = (time: string): number | null => {
  const trimmed = time.trim();
  if (!trimmed) return null;
  const match = TIME_OF_DAY.exec(trimmed);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

const startOfLocalDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const toValidDate = (value: Date | null | undefined): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export type EventDateFields = {
  eventStartDate?: Date | null;
  eventStartTime?: string;
  eventEndDate?: Date | null;
  eventEndTime?: string;
};

/**
 * Returns field errors when an optional end is before the required start: an
 * end day before the start day goes on the end date; on one day (no end date,
 * or the start date again) an end time before the start time goes on the end time.
 */
export function getEventEndBeforeStartErrors(
  formData: EventDateFields
): { eventEndDate?: string; eventEndTime?: string } {
  const { eventStartDate, eventStartTime, eventEndDate, eventEndTime } =
    formData;

  const start = toValidDate(eventStartDate);
  if (!start) {
    return {};
  }

  if (eventEndDate) {
    const end = toValidDate(eventEndDate);
    if (!end) return {};
    const startDay = startOfLocalDay(start);
    const endDay = startOfLocalDay(end);
    if (endDay.getTime() < startDay.getTime()) {
      return { eventEndDate: EVENT_END_BEFORE_START_ERROR };
    }
    if (endDay.getTime() > startDay.getTime()) {
      return {};
    }
  }

  const startMins = eventStartTime ? timeToMinutes(eventStartTime) : null;
  const endMins = eventEndTime ? timeToMinutes(eventEndTime) : null;
  if (startMins !== null && endMins !== null && endMins < startMins) {
    return { eventEndTime: EVENT_END_TIME_BEFORE_START_ERROR };
  }

  return {};
}

/** Returns a field error when the event start date is before today (local calendar day). */
export function getEventStartBeforeTodayErrors(
  formData: Pick<EventDateFields, "eventStartDate">,
  now: Date = new Date()
): { eventStartDate?: string } {
  const start = toValidDate(formData.eventStartDate);
  if (!start) {
    return {};
  }

  const startDay = startOfLocalDay(start);
  const today = startOfLocalDay(now);

  if (startDay.getTime() < today.getTime()) {
    return { eventStartDate: EVENT_START_BEFORE_TODAY_ERROR };
  }

  return {};
}

/** Local event start as epoch ms (date + optional HH:MM), or null if unknown. */
export function getEventStartDateTimeMs(
  eventStartDate?: Date | string | null,
  eventStartTime?: string
): number | null {
  const start = toValidDate(
    eventStartDate instanceof Date
      ? eventStartDate
      : eventStartDate
        ? new Date(eventStartDate)
        : null
  );
  if (!start) return null;

  const y = start.getFullYear();
  const m = String(start.getMonth() + 1).padStart(2, "0");
  const d = String(start.getDate()).padStart(2, "0");
  const datePart = `${y}-${m}-${d}`;
  const startStr = eventStartTime?.trim()
    ? `${datePart}T${eventStartTime.trim()}`
    : datePart;
  const startMs = new Date(startStr).getTime();
  return Number.isFinite(startMs) ? startMs : null;
}

/** Error when publishAt is after the first event occurrence start. */
export function getPublishAfterEventStartError(
  publishAt: string | undefined,
  eventStartDate?: Date | string | null,
  eventStartTime?: string
): string | undefined {
  if (!publishAt?.trim()) return undefined;

  const publishMs = new Date(publishAt).getTime();
  if (Number.isNaN(publishMs)) return undefined;

  const eventStartMs = getEventStartDateTimeMs(
    eventStartDate,
    eventStartTime
  );
  if (eventStartMs === null) return undefined;

  if (publishMs > eventStartMs) {
    return PUBLISH_AFTER_EVENT_START_ERROR;
  }

  return undefined;
}
